import { NewActionMenu } from "@/features/actions";
import { BriefingSlot } from "@/features/briefing";
import { useGoToIntegrations } from "@/features/connectors";
import { validateFinanceSearch } from "@/features/workspace-router";
import { IndicatorRow } from "@/features/workspace/components/IndicatorRow";
import { SubTabBar, useActiveSubTab } from "@/features/workspace/components/SubTabBar";
import { FinanceSearchFilters } from "@/features/workspace/components/searchFilters";
import type { SubTabDef } from "@/features/workspace/config";
import {
  AskAndoCardGrid,
  AskAndoSectionLabel,
  BankAccountsList,
  CashFlowWeekTable,
  CategorySpendBreakdown,
  ConnectionsPanel,
  MiniMetric,
  RecentTransactionsList,
  SyncStatusPanel,
  useBankAccounts,
  useBankingSummary,
  useMonthlyAggregates,
  useRecentTransactions,
  useTriggerAllBankSync,
  useTriggerBankSync,
} from "@/features/workspace/finance";
import { useActiveConnections } from "@/features/workspace/hooks/useActiveConnections";
import { financeOverviewQueryOptions } from "@/features/workspace/hooks/useFinanceOverview";
import {
  FINANCE_PROVIDER_COLORS,
  type FinanceConnection,
  useFinanceConnections,
} from "@/features/workspace/hooks/useFinance";
import {
  useCreateBalanceSnapshot,
  useFinanceDeadlines,
  useFinanceOverview,
  useRefreshFinanceOverview,
} from "@/features/workspace/hooks/useFinanceOverview";
import { useSearchNavigation } from "@/features/workspace/hooks/useSearchNavigation";
import type {
  BurnRateMetric,
  CashFlowBucket,
  FinanceDeadline,
  FinancePeriod,
  OpenPayablesSummary,
  OpenReceivablesSummary,
  SalesforcePipelineSummary,
} from "@ando/core";
import {
  Button,
  ConnectionPicker,
  type ConnectionPickerOption,
  ConsentExpiryBanner,
  DateTimePicker,
  HighlightNote,
  PencilCard,
  SegmentedControl,
  SurfaceCommandBar,
  SurfaceEmptyState,
  SurfaceFiltersPopover,
  SurfaceLayout,
  WorkspaceSearchBar,
  consentExpiryUrgency,
  dateToYmd,
  ymdToDate,
} from "@ando/ui";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { Link as LinkIcon, RefreshCw, Settings2, Sparkles, Wallet } from "lucide-react";
import type * as React from "react";
import { useCallback, useMemo, useState } from "react";

export const Route = createFileRoute("/_authed/workspace/finance")({
  validateSearch: validateFinanceSearch,
  // Warm the default-period ("13w") finance overview on hover/focus so the
  // hub paints from cache; user period switches still client-fetch.
  loader: ({ context: { queryClient } }) => {
    void queryClient.ensureQueryData(financeOverviewQueryOptions());
  },
  component: FinanceHub,
});

/**
 * Finance hub — cash-flow forecast + Adeguati Assetti indicators.
 *
 * v1 reference: fragments live across ``FattureInCloudPage.tsx`` (invoice
 * lists) + a planned "Adeguati Assetti" dashboard that hasn't shipped.
 *
 * BE source (May 2026 — cdb0999): ``GET /api/v1/finance/overview``
 * computes cash-flow + DSCR + burn-rate from the connected
 * ``open_banking`` (primary) and ``accounting`` (FiC fallback)
 * connectors. The BE returns 200 with zeroed fields and
 * ``coverage_pct=0`` when no connector is connected — the hub renders
 * the empty state below in that case.
 */

const PERIOD_LABELS: Record<FinancePeriod, MessageDescriptor> = {
  "4w": msg`4 sett.`,
  "13w": msg`13 sett.`,
  "26w": msg`26 sett.`,
  "52w": msg`52 sett.`,
};

const PERIOD_ORDER: readonly FinancePeriod[] = ["4w", "13w", "26w", "52w"];

function FinanceHub() {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const goToIntegrations = useGoToIntegrations();
  const search = useSearch({ from: "/_authed/workspace/finance" });
  // DRY URL-patcher — same hook as conversations.tsx / documents.tsx.
  const searchNav = useSearchNavigation<{
    type: ReadonlyArray<string>;
    status: ReadonlyArray<string>;
    period: string;
    amount_min: number | null;
    amount_max: number | null;
  }>("/workspace/finance");
  const [period, setPeriod] = useState<FinancePeriod>("13w");
  // ``setBriefingHasContent`` still feeds ``BriefingSlot onLoaded``; the value
  // itself is no longer read (its only consumer was the CompanionHero, removed
  // with the Jul 2026 command-bar de-layering).
  const [, setBriefingHasContent] = useState(false);
  const { data, isLoading, isError } = useFinanceOverview(period);
  const refresh = useRefreshFinanceOverview(period);
  const activeSubTab = useActiveSubTab("finance");
  const { data: deadlinesData } = useFinanceDeadlines(30);

  // ── Connection picker plumbing ────────────────────────────────────────
  // Aggregate every finance connection (open banking, FiC, Salesforce,
  // Aruba SDI, Acube cassetto …) into one typed array — mirror of
  // ``useCalendarConnections`` but heterogenous across capabilities.
  const { connections: financeConnections } = useFinanceConnections();

  // Map BE ``FinanceConnection`` onto the picker's structural contract.
  // Memoised so ``useActiveConnections`` doesn't see a fresh array
  // reference on every render and re-run its reconciliation effect.
  const connectionOptions = useMemo<readonly ConnectionPickerOption[]>(
    () =>
      financeConnections.map<ConnectionPickerOption>((c) => ({
        id: c.id,
        provider: c.provider,
        account_label: c.account_label,
      })),
    [financeConnections],
  );

  const {
    activeIds,
    setActiveIds,
    isReady: connsReady,
  } = useActiveConnections("finance", connectionOptions);

  // O(1) lookup keyed by connection id — drives both the
  // ``getConnectionColor`` resolver (visual hint on panels) and the
  // dynamic "is X connected?" derivations below that replace the v1
  // ``completeness.banking_connected`` boolean.
  const connectionByid = useMemo(
    () => new Map(financeConnections.map((c) => [c.id, c])),
    [financeConnections],
  );

  // Set of ACTIVE finance-capability slots — drives panel visibility +
  // the SubTabBar badges. Computed from the active picker selection,
  // NOT from ``data_completeness`` — so the user filtering down to "just
  // FiC" instantly silences the banking panel.
  const activeCapabilities = useMemo<Set<FinanceConnection["capability"]>>(() => {
    const set = new Set<FinanceConnection["capability"]>();
    for (const id of activeIds) {
      const conn = connectionByid.get(id);
      if (conn) set.add(conn.capability);
    }
    return set;
  }, [activeIds, connectionByid]);

  /** True iff the user has selected at least one open-banking account. */
  const bankingConnected = activeCapabilities.has("open_banking");
  /** True iff the user has selected at least one Fatture in Cloud company. */
  const ficConnected = activeCapabilities.has("accounting");
  /** True iff the user has selected at least one Salesforce / CRM env. */
  const crmConnected = activeCapabilities.has("crm");
  /** True iff at least one fiscal connector (FiC / SDI / Cassetto) is active. */
  const fiscalConnected =
    ficConnected ||
    activeCapabilities.has("sdi_invoicing") ||
    activeCapabilities.has("cassetto_fiscale");
  /** True iff the Cassetto Fiscale connector is wired — surfaces "ask the AI"
   * CTAs in the F24 / Prossime scadenze panels (the BE specialist now
   * answers structured queries about fatture passive / attive / F24 via
   * chat — see ``CassettoFiscaleSpecialist``). */
  const cassettoConnected = activeCapabilities.has("cassetto_fiscale");

  // Resolve the brand-colour hint for a given connection id — drives
  // border-left accents on KPI cards / panel headers. Falls back to a
  // neutral so unattributed UI stays readable.
  const getConnectionColor = useCallback(
    (connectionId: string | null): string => {
      if (!connectionId) return "#FF7A1A";
      const conn = connectionByid.get(connectionId);
      if (!conn) return "#FF7A1A";
      if (FINANCE_PROVIDER_COLORS[conn.provider]) {
        return FINANCE_PROVIDER_COLORS[conn.provider]!;
      }
      return "#FF7A1A";
    },
    [connectionByid],
  );
  // Silence unused-warning until panel widgets adopt the colour hint —
  // exposed so downstream components (balance card, invoice row, lead
  // card) can border-left-tint themselves without re-importing the map.
  void getConnectionColor;

  const completeness = data?.data_completeness;
  // Extended-panel signal: when any of the v2 sources is present we are
  // NOT in the "anteprima" empty state even if coverage_pct is 0.
  const hasV2Source =
    !!data?.open_receivables ||
    !!data?.open_payables ||
    !!data?.salesforce_pipeline ||
    !!data?.adequate_setup;
  const noData = !completeness || (completeness.coverage_pct === 0 && !hasV2Source);
  // Distinct empty: user HAS connections but has filtered them all out
  // via the picker. We keep the hub visible (header + picker stay in
  // place) and surface a soft "Nessun collegamento attivo" body so the
  // user understands the panels are silent on purpose.
  const noActiveSelection = connsReady && connectionOptions.length > 0 && activeIds.length === 0;
  const currency = data?.currency ?? "EUR";

  // Sub-tab visibility: each chip toggles which panels are rendered. The
  // KPI strip (DSCR / burn-rate / runway) stays visible on every tab as
  // an "always-on" summary so the operator never loses context.
  // ``showCashFlow`` still drives the sparkline panel (visible on both
  // ``overview`` and the dedicated ``cashflow`` tab); the legacy
  // ``showBankingPanel`` / ``showFicPanel`` / ``showF24`` booleans were
  // collapsed into the dedicated ``BankingTabBody`` / ``SalesTabBody`` /
  // ``FiscalTabBody`` sub-components rendered below — they own their
  // own connection list + Ask-Ando CTAs + sub-tab-specific content.
  const showCashFlow = activeSubTab === "overview" || activeSubTab === "cashflow";

  // Sub-tab chips (UI/UX audit 2026-05-30): labels follow the SMB-italian
  // lessico (Panoramica / Banca / Vendite / Fisco) instead of vendor /
  // protocol jargon (Overview / Banking / SDI / F24). The chip ``id``
  // values stay frozen so URL deep-links + analytics regressions are
  // impossible — only the user-facing copy moves.
  const chips = useMemo<readonly SubTabDef[]>(
    () => [
      { id: "overview", label: i18n._(msg`Panoramica`) },
      {
        id: "banking",
        label: i18n._(msg`Banca`),
        // Dynamic: hide the "0" badge as soon as the user activates at
        // least one open-banking connection. Replaces the BE
        // ``completeness.banking_connected`` boolean so filtering down
        // to FiC-only no longer leaves a stale "connected" badge.
        badge: bankingConnected ? undefined : 0,
      },
      {
        id: "sdi",
        label: i18n._(msg`Fatture`),
        badge: fiscalConnected ? undefined : 0,
      },
      { id: "f24", label: i18n._(msg`Fisco`) },
      { id: "cashflow", label: i18n._(msg`Cash flow`) },
    ],
    [bankingConnected, fiscalConnected, i18n],
  );

  return (
    <SurfaceLayout
      header={
        // Jul 2026 — single-row command bar (replaces stacked header + hero +
        // search + filter rows). The nav tab names the area; ``count`` carries
        // the summary; filters live behind the shared Filtri popover.
        <SurfaceCommandBar
          count={
            isLoading ? (
              <span className="text-ink-3">
                <Trans>Caricamento…</Trans>
              </span>
            ) : isError ? (
              <span className="text-rose-700">
                <Trans>Errore nel recupero dati</Trans>
              </span>
            ) : noData ? (
              <span className="text-ink-3">
                <Trans>Anteprima — collega un connettore</Trans>
              </span>
            ) : (
              <span className="text-ink-3 tabular-nums">
                <Trans>
                  Aggiornato {formatAsOf(data?.as_of)} · copertura{" "}
                  {Math.round((completeness?.coverage_pct ?? 0) * 100)}%
                </Trans>
              </span>
            )
          }
          search={
            <WorkspaceSearchBar
              placeholder={i18n._(msg`Cerca fatture, banking, F24…`)}
              ariaLabel={i18n._(msg`Cerca finanza`)}
              initialQuery={search.q}
              onSearch={searchNav.setQuery}
            />
          }
          views={<SubTabBar tabId="finance" chips={chips} className="!px-0 !py-0" />}
          filters={
            <SurfaceFiltersPopover
              ariaLabel={i18n._(msg`Filtri amministrazione`)}
              label={<Trans>Filtri</Trans>}
              activeCount={
                (search.type?.length ?? 0) +
                (search.status?.length ?? 0) +
                ((search.period ?? "all") !== "all" ? 1 : 0) +
                (search.amount_min != null ? 1 : 0) +
                (search.amount_max != null ? 1 : 0)
              }
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <FinanceSearchFilters
                  type={search.type ?? []}
                  status={search.status ?? []}
                  period={search.period ?? "all"}
                  amount_min={search.amount_min ?? null}
                  amount_max={search.amount_max ?? null}
                  onChange={searchNav.patchFilters}
                />
              </div>
            </SurfaceFiltersPopover>
          }
          range={
            <SegmentedControl<FinancePeriod>
              options={PERIOD_ORDER.map((p) => ({ value: p, label: i18n._(PERIOD_LABELS[p]) }))}
              value={period}
              onChange={setPeriod}
              ariaLabel={i18n._(msg`Periodo di previsione`)}
            />
          }
          actions={
            <div className="flex items-center gap-2">
              {connectionOptions.length > 0 ? (
                <ConnectionPicker
                  connections={connectionOptions}
                  activeIds={activeIds}
                  onChange={setActiveIds}
                  colorMap={FINANCE_PROVIDER_COLORS}
                  label={
                    connectionOptions.length === 1
                      ? i18n._(msg`1 collegamento`)
                      : i18n._(msg`${activeIds.length}/${connectionOptions.length} collegamenti`)
                  }
                  ariaLabel={i18n._(msg`Seleziona collegamenti finanziari`)}
                />
              ) : null}
              <button
                type="button"
                onClick={() => refresh.mutate()}
                disabled={refresh.isPending}
                className="inline-flex items-center gap-1.5 rounded-md border border-hairline bg-card/60 px-2.5 py-1 font-medium text-[11px] text-ink-2 hover:text-ink-1 disabled:opacity-50"
                aria-label={i18n._(msg`Aggiorna dati`)}
              >
                <RefreshCw
                  size={12}
                  className={refresh.isPending ? "animate-spin" : undefined}
                  aria-hidden
                />
                <Trans>Aggiorna</Trans>
              </button>
              <NewActionMenu
                area="finance"
                label={i18n._(msg`Nuovo`)}
                extraItems={[
                  {
                    id: "finance:configure_connectors",
                    label: i18n._(msg`Configura connettori`),
                    icon: Settings2,
                    onSelect: () => goToIntegrations("finance"),
                  },
                ]}
              />
            </div>
          }
        />
      }
    >
      {/* Consent expiry banners — capability-agnostic, one row per
          connection expiring within 14d. The hook returns ``expires_at``
          per row; the shared banner stays silent when there's nothing
          to renew so this block is a no-op on healthy connections. */}
      <FinanceConsentBanners
        connections={financeConnections}
        onRenew={() => goToIntegrations("finance")}
      />
      {/* Briefing slot — proactive "Brief & Deep" card for the finance
          domain. Silent on BE 404 / empty payload. */}
      <BriefingSlot domain="finance" className="mb-5" onLoaded={setBriefingHasContent} />
      {noData ? (
        <FinanceEmptyOnboarding onConnect={() => goToIntegrations("finance")} />
      ) : noActiveSelection ? (
        <FinanceNoActiveSelection
          onSelectAll={() => setActiveIds(connectionOptions.map((c) => c.id))}
        />
      ) : (
        <>
          {/* KPI grid — v2: Crediti / Debiti / Pipeline SF (mockup r3193).
              Falls back to DSCR / Burn / Runway when no v2 source is
              connected, so the chip never lands on an empty state. */}
          <PencilCard className="mb-5 grid grid-cols-3 divide-x divide-hairline overflow-hidden">
            {hasV2Source ? (
              <>
                <KpiCard
                  label={i18n._(msg`Crediti aperti`)}
                  value={
                    data?.open_receivables
                      ? formatCurrencyFromCents(data.open_receivables.total_eur_cents, currency)
                      : "—"
                  }
                  sub={
                    data?.open_receivables
                      ? i18n._(
                          msg`${data.open_receivables.invoice_count} fatture · ${data.open_receivables.expiring_soon_count} in scadenza`,
                        )
                      : i18n._(msg`richiede Fatture in Cloud`)
                  }
                />
                <KpiCard
                  label={i18n._(msg`Debiti aperti`)}
                  value={
                    data?.open_payables
                      ? formatCurrencyFromCents(data.open_payables.total_eur_cents, currency)
                      : "—"
                  }
                  sub={
                    data?.open_payables
                      ? i18n._(msg`${data.open_payables.invoice_count} fatture passive`)
                      : i18n._(msg`richiede Fatture in Cloud`)
                  }
                />
                <KpiCard
                  label={i18n._(msg`Pipeline SF`)}
                  value={
                    data?.salesforce_pipeline
                      ? formatCurrencyFromCents(data.salesforce_pipeline.total_eur_cents, currency)
                      : "—"
                  }
                  sub={
                    data?.salesforce_pipeline
                      ? data.salesforce_pipeline.won_this_quarter_eur_cents > 0
                        ? i18n._(
                            msg`${data.salesforce_pipeline.deal_count} deal · ${data.salesforce_pipeline.closing_soon_count} in chiusura · ${formatCurrencyFromCents(
                              data.salesforce_pipeline.won_this_quarter_eur_cents,
                              currency,
                            )} vinti Q${Math.floor(new Date().getMonth() / 3) + 1}`,
                          )
                        : i18n._(
                            msg`${data.salesforce_pipeline.deal_count} deal · ${data.salesforce_pipeline.closing_soon_count} in chiusura`,
                          )
                      : i18n._(msg`richiede Salesforce`)
                  }
                />
              </>
            ) : (
              <>
                <KpiCard
                  label={i18n._(msg`DSCR 12 mesi`)}
                  value={data?.dscr ? formatNumber(data.dscr.current, 2) : "—"}
                  sub={
                    data?.dscr
                      ? (() => {
                          const interp =
                            data.dscr.interpretation === "healthy"
                              ? i18n._(msg`Solido`)
                              : data.dscr.interpretation === "watch"
                                ? i18n._(msg`Da monitorare`)
                                : i18n._(msg`Sotto stress`);
                          return i18n._(
                            msg`${interp} · Δ30g ${formatDelta(data.dscr.trend_delta_30d)}`,
                          );
                        })()
                      : i18n._(msg`dati insufficienti`)
                  }
                />
                <KpiCard
                  label={i18n._(msg`Burn mensile`)}
                  value={formatCurrency(data?.burn_rate?.monthly_eur, currency)}
                  sub={
                    data?.burn_rate
                      ? i18n._(
                          msg`Δ30g ${formatCurrencyDelta(data.burn_rate.trend_delta_30d, currency)}`,
                        )
                      : i18n._(msg`richiede open banking`)
                  }
                />
                <KpiCard
                  label={i18n._(msg`Runway`)}
                  value={
                    nullableNum(data?.burn_rate?.runway_months) !== null
                      ? i18n._(msg`${formatNumber(data?.burn_rate?.runway_months, 1)} mesi`)
                      : "—"
                  }
                  sub={
                    nullableNum(data?.burn_rate?.runway_months) !== null
                      ? safeNum(data?.burn_rate?.runway_months) >= 12
                        ? i18n._(msg`Oltre l'anno`)
                        : i18n._(msg`Sotto l'anno`)
                      : "—"
                  }
                />
              </>
            )}
          </PencilCard>

          {/* Forecast panel — real sparkline from `cash_flow` buckets.
              Visible on `overview` + `cashflow`. */}
          {showCashFlow ? (
            <Panel>
              <PanelHead
                left={
                  <>
                    <div className="mb-2 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
                      <Trans>Cash-flow {i18n._(PERIOD_LABELS[period])}</Trans>
                    </div>
                    <div className="font-semibold text-[15px] text-ink-1">
                      <Trans>
                        {formatCurrency(
                          data?.cash_flow?.reduce((acc, b) => acc + b.net, 0) ?? 0,
                          currency,
                        )}{" "}
                        netto
                      </Trans>
                    </div>
                  </>
                }
                right={
                  <span className="text-[10px] text-ink-3 tabular-nums">
                    <Trans>{data?.cash_flow.length ?? 0} settimane</Trans>
                  </span>
                }
              />
              <Sparkline buckets={data?.cash_flow ?? []} />
            </Panel>
          ) : null}

          {/* Adeguati Assetti · Codice Crisi — mockup r3232..3269. Render
              only when the BE returned at least one indicator (i.e. some
              connector input materialised). */}
          {activeSubTab === "overview" && data?.adequate_setup ? (
            <Panel>
              <PanelHead
                left={
                  <>
                    <div className="mb-2 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
                      <Trans>Adeguati Assetti · Codice Crisi</Trans>
                    </div>
                    <div className="font-semibold text-[15px] text-ink-1">
                      {summariseIndicators(data.adequate_setup.indicators, i18n)}
                    </div>
                  </>
                }
              />
              <div className="flex flex-col">
                {data.adequate_setup.indicators.map((ind) => (
                  <IndicatorRow
                    key={ind.label}
                    label={ind.label}
                    valueDisplay={ind.value_display}
                    barPercent={ind.bar_percent}
                    severity={ind.severity}
                  />
                ))}
              </div>
              {/* Patrimonio netto CTA — surfaces when the BE returned the
                  panel but no user-supplied equity snapshot exists. The
                  Codice della Crisi indicator can't be auto-derived from
                  any connector, so we collect it inline. */}
              {!data.equity_snapshot ? <BalanceSnapshotInlineForm /> : null}
            </Panel>
          ) : null}

          {/* Prossime scadenze — mockup r3271..3289. When the BE has no
              fiscal source the list is empty and we render a placeholder
              instead of an empty box. */}
          {activeSubTab === "overview" || activeSubTab === "f24" ? (
            <Panel>
              <div className="mb-3 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
                <Trans>Prossime scadenze</Trans>
              </div>
              {deadlinesData && deadlinesData.items.length > 0 ? (
                <div className="flex flex-col">
                  {deadlinesData.items.map((d, idx) => (
                    <DeadlineRow key={`${d.kind}-${d.due_date}-${idx}`} item={d} />
                  ))}
                </div>
              ) : cassettoConnected ? (
                <CassettoAskAndoBlock
                  navigate={navigate}
                  primary={i18n._(
                    msg`Cassetto Fiscale collegato — chiedi ad Ando di interrogare l'archivio AdE.`,
                  )}
                  prompt={i18n._(
                    msg`Mostrami le scadenze fiscali (F24, LIPE, IVA) dei prossimi 30 giorni dal Cassetto Fiscale.`,
                  )}
                  cta={i18n._(msg`Apri chat — scadenze fiscali`)}
                />
              ) : (
                <p className="text-ink-3 text-sm">
                  <Trans>
                    Collega il Cassetto Fiscale per visualizzare F24, LIPE e conservazione in
                    arrivo.
                  </Trans>
                </p>
              )}
            </Panel>
          ) : null}

          {/* ── BANCA — open banking density pass (June 2026) ────── */}
          {activeSubTab === "banking" ? (
            <BankingTabBody
              connections={financeConnections.filter((c) => c.capability === "open_banking")}
              cashFlow={data?.cash_flow ?? []}
              burnRate={data?.burn_rate ?? null}
              currency={currency}
              onConnect={() => goToIntegrations("finance")}
            />
          ) : null}

          {/* ── VENDITE — accounting + invoicing + CRM density pass ── */}
          {activeSubTab === "sdi" ? (
            <SalesTabBody
              connections={financeConnections.filter(
                (c) =>
                  c.capability === "accounting" ||
                  c.capability === "sdi_invoicing" ||
                  c.capability === "crm",
              )}
              receivables={data?.open_receivables ?? null}
              payables={data?.open_payables ?? null}
              pipeline={data?.salesforce_pipeline ?? null}
              currency={currency}
              onConnect={() => goToIntegrations("finance")}
            />
          ) : null}

          {/* ── FISCO — cassetto + deadlines + ask-Ando CTAs ───────── */}
          {activeSubTab === "f24" ? (
            <FiscalTabBody
              connections={financeConnections.filter(
                (c) => c.capability === "cassetto_fiscale" || c.capability === "sdi_invoicing",
              )}
              deadlines={deadlinesData?.items ?? []}
              cassettoConnected={cassettoConnected}
              onConnect={() => goToIntegrations("finance")}
            />
          ) : null}

          {/* ── CASH FLOW — sparkline already shown above; add the
                weekly breakdown table for full info density. */}
          {activeSubTab === "cashflow" ? (
            <Panel>
              <div className="mb-3 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
                <Trans>Dettaglio settimanale</Trans>
              </div>
              <CashFlowWeekTable buckets={data?.cash_flow ?? []} currency={currency} />
            </Panel>
          ) : null}

          {/* ── PANORAMICA — keep the legacy compact source-coverage card
                AND surface a connections-list snapshot on top so the user
                immediately sees what's wired. */}
          {activeSubTab === "overview" ? (
            <>
              <ConnectionsPanel
                title={i18n._(msg`Collegamenti attivi`)}
                connections={financeConnections}
                emptyTitle={i18n._(msg`Nessun collegamento configurato`)}
                emptyDescription={i18n._(
                  msg`Banca, fatture, CRM e cassetto fiscale popoleranno i grafici sopra una volta collegati.`,
                )}
                onConnect={() => goToIntegrations("finance")}
              />
              <Panel>
                <PanelHead
                  left={
                    <>
                      <div className="mb-2 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
                        <Trans>Sorgenti dati</Trans>
                      </div>
                      <div className="font-semibold text-[15px] text-ink-1">
                        <Trans>
                          Copertura {Math.round((completeness?.coverage_pct ?? 0) * 100)}%
                        </Trans>
                      </div>
                    </>
                  }
                />
                <div className="flex flex-col gap-3.5">
                  <SourceIndicator label="Fatture in Cloud" connected={ficConnected} />
                  <SourceIndicator
                    label={i18n._(msg`Open banking (PSD2)`)}
                    connected={bankingConnected}
                  />
                  <SourceIndicator label="Salesforce CRM" connected={crmConnected} />
                </div>
              </Panel>
            </>
          ) : null}
        </>
      )}
    </SurfaceLayout>
  );
}

// ── Helpers — formatting + small components ───────────────────────────────

/**
 * Defensive numeric guard for every BE field consumed by this hub.
 *
 * The wire types declare ``runway_months: number`` / ``monthly_eur:
 * number`` / etc. as required, but the BE OCCASIONALLY emits ``null``
 * (or a non-finite value) when the underlying source has too few
 * transactions to compute the metric. Calling ``.toFixed`` on a ``null``
 * crashes the entire route (June 2026 incident on the Banca tab).
 *
 * Use this helper at EVERY arithmetic / formatting boundary that
 * touches a BE-supplied number.
 */
function safeNum(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/** Same as :func:`safeNum` but returns ``null`` when the value isn't
 *  finite — pair with a `?? "—"` at the call site. */
function nullableNum(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function formatCurrency(value: number | null | undefined, currency: string): string {
  const safe = nullableNum(value);
  if (safe === null) return "—";
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(safe);
}

/** EUR cents → "€12.450" (Italian thousands separator, no decimals).
 *  Safe against null/NaN cents. */
function formatCurrencyFromCents(cents: number | null | undefined, currency: string): string {
  const safe = nullableNum(cents);
  if (safe === null) return "—";
  return formatCurrency(Math.round(safe) / 100, currency);
}

/** Build the "3 indicatori sani · 1 attenzione" panel header line. */
function summariseIndicators(
  indicators: readonly { readonly severity: "success" | "warning" | "danger" }[],
  i18n: ReturnType<typeof useLingui>["i18n"],
): string {
  const counts = { success: 0, warning: 0, danger: 0 };
  for (const i of indicators) counts[i.severity] += 1;
  const segments: string[] = [];
  if (counts.success > 0) segments.push(i18n._(msg`${counts.success} sani`));
  if (counts.warning > 0) segments.push(i18n._(msg`${counts.warning} attenzione`));
  if (counts.danger > 0) segments.push(i18n._(msg`${counts.danger} critici`));
  return segments.length > 0 ? segments.join(" · ") : i18n._(msg`Nessun indicatore`);
}

function formatCurrencyDelta(delta: number | null | undefined, currency: string): string {
  const safe = nullableNum(delta);
  if (safe === null) return "—";
  const sign = safe >= 0 ? "+" : "";
  return `${sign}${formatCurrency(safe, currency)}`;
}

function formatDelta(delta: number | null | undefined): string {
  const safe = nullableNum(delta);
  if (safe === null) return "—";
  const sign = safe >= 0 ? "+" : "";
  return `${sign}${safe.toFixed(2)}`;
}

/** Safe ``toFixed`` — returns "—" when the value isn't finite. */
function formatNumber(value: number | null | undefined, digits = 1): string {
  const safe = nullableNum(value);
  if (safe === null) return "—";
  return safe.toFixed(digits);
}

function formatAsOf(iso: string | undefined): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

interface KpiCardProps {
  label: string;
  value: string;
  sub: string;
}

// Pencil (Jul 2026): one hairline-divided cell inside the KPI PencilCard strip
// (mirrors SalesOverview.StatCell) — no per-card box/shadow, compact number.
const KpiCard: React.FC<KpiCardProps> = ({ label, value, sub }) => (
  <div className="px-4 py-3.5">
    <div className="mb-1.5 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
      {label}
    </div>
    <div className="font-semibold text-[19px] text-ink-1 tabular-nums tracking-[-0.02em]">
      {value}
    </div>
    <div className="mt-1 text-[10px] text-ink-3 tabular-nums">{sub}</div>
  </div>
);

interface SparklineProps {
  buckets: readonly CashFlowBucket[];
}

/**
 * Lightweight inline sparkline for the cash-flow panel. SVG-only, no deps.
 * Skips rendering when there are fewer than 2 points (the BE returns an
 * empty array on insufficient data; the empty state above handles 0).
 */
const Sparkline: React.FC<SparklineProps> = ({ buckets }) => {
  const { i18n } = useLingui();
  // Defensive: BE may emit ``net: null`` on freshly-created accounts (no
  // bookings yet). The previous version trusted ``buckets`` to always be
  // a non-empty array of well-typed objects and crashed with "Cannot read
  // properties of undefined (reading 'split')" the first time a single
  // entry slipped through unparsed. Coerce + filter at the boundary;
  // render the placeholder when no point survives.
  const safeBuckets = Array.isArray(buckets) ? buckets : [];
  const points: string[] = [];
  if (safeBuckets.length >= 2) {
    const nets = safeBuckets.map((b) =>
      typeof b?.net === "number" && Number.isFinite(b.net) ? b.net : 0,
    );
    const min = Math.min(...nets, 0);
    const max = Math.max(...nets, 0);
    const range = max - min || 1;
    for (let i = 0; i < nets.length; i++) {
      const x = (i / (safeBuckets.length - 1)) * 600;
      const y = 100 - ((nets[i]! - min) / range) * 80;
      if (Number.isFinite(x) && Number.isFinite(y)) {
        points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
      }
    }
  }

  if (points.length < 2) {
    return (
      <div className="flex h-[96px] items-center justify-center text-[11px] text-ink-3">
        <Trans>Dati insufficienti per il grafico</Trans>
      </div>
    );
  }

  const last = points[points.length - 1]!;
  const first = points[0]!;
  const [lastX, lastY] = last.split(",");
  const firstX = first.split(",")[0];

  return (
    <svg
      viewBox="0 0 600 120"
      preserveAspectRatio="none"
      className="block h-[96px] w-full"
      role="img"
      aria-label={i18n._(msg`Cash flow netto settimanale`)}
    >
      <defs>
        <linearGradient id="finance-sparkline-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FF7A1A" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#FF7A1A" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d={`M${points.join(" L")} L${lastX},120 L${firstX},120 Z`}
        fill="url(#finance-sparkline-grad)"
      />
      <path d={`M${points.join(" L")}`} fill="none" stroke="#FF7A1A" strokeWidth="1.5" />
      <circle cx={lastX} cy={lastY} r="4" fill="#FF7A1A" />
    </svg>
  );
};

interface SourceIndicatorProps {
  label: string;
  connected: boolean;
}

const SourceIndicator: React.FC<SourceIndicatorProps> = ({ label, connected }) => (
  <div className="flex items-center gap-3">
    <span
      aria-hidden
      className={`h-1.5 w-1.5 shrink-0 rounded-full ${connected ? "bg-success" : "bg-ink-1/30"}`}
    />
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between text-[12px] text-ink-2">
        <span>{label}</span>
        <span className={`font-medium tabular-nums ${connected ? "text-success" : "text-ink-3"}`}>
          {connected ? <Trans>Collegato</Trans> : <Trans>Non collegato</Trans>}
        </span>
      </div>
    </div>
  </div>
);

// ── Local DRY helpers ────────────────────────────────────────────────────

interface DeadlineRowProps {
  item: FinanceDeadline;
}

/**
 * One row of the "Prossime scadenze" panel. Mirrors mockup
 * ``ando_v18.html`` rows 3274..3288 (``.scad-row`` block).
 */
const DeadlineRow: React.FC<DeadlineRowProps> = ({ item }) => {
  const { i18n } = useLingui();
  const STATUS_LABEL: Record<FinanceDeadline["status"], string> = {
    upcoming: i18n._(msg`in arrivo`),
    due_today: i18n._(msg`in scadenza oggi`),
    overdue: i18n._(msg`scaduta`),
    ready_to_submit: i18n._(msg`pronta per invio`),
  };
  const due = new Date(`${item.due_date}T00:00:00`);
  const day = due.getDate().toString();
  const monthShort = new Intl.DateTimeFormat("it-IT", { month: "short" })
    .format(due)
    .replace(".", "");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffMs = due.getTime() - today.getTime();
  const days = Math.round(diffMs / (24 * 3600 * 1000));
  const badge =
    item.status === "overdue"
      ? i18n._(msg`in ritardo ${Math.abs(days)}g`)
      : days <= 0
        ? i18n._(msg`oggi`)
        : i18n._(msg`in ${days}g`);
  const amountSuffix =
    item.amount_eur_cents != null
      ? ` · ${formatCurrencyFromCents(item.amount_eur_cents, "EUR")}`
      : "";
  return (
    <div className="flex items-center gap-3 border-hairline/60 border-b py-2.5 last:border-b-0">
      <div className="w-9 shrink-0 text-center">
        <div className="font-semibold text-[15px] text-ink-1 tabular-nums">{day}</div>
        <div className="font-medium text-[9px] text-ink-3 uppercase tracking-wider">
          {monthShort}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-[13px] text-ink-1">{item.label}</div>
        <div className="text-[11px] text-ink-3">
          {STATUS_LABEL[item.status]}
          {amountSuffix}
        </div>
      </div>
      <span className="rounded-md border border-hairline bg-card/60 px-2 py-0.5 font-medium text-[10px] text-ink-2 tabular-nums">
        {badge}
      </span>
    </div>
  );
};
DeadlineRow.displayName = "DeadlineRow";

interface PanelProps {
  children: React.ReactNode;
}

// Pencil (Jul 2026): warm-paper PencilCard (shadow-as-hairline) instead of the
// old chunky white/border-black box — the sanctioned finance-hub surface.
const Panel: React.FC<PanelProps> = ({ children }) => (
  <PencilCard className="mb-4 p-5">{children}</PencilCard>
);

interface PanelHeadProps {
  left: React.ReactNode;
  right?: React.ReactNode;
}

const PanelHead: React.FC<PanelHeadProps> = ({ left, right }) => (
  <div className="mb-3.5 flex items-end justify-between gap-3">
    <div className="min-w-0">{left}</div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

// ── Empty / onboarding state ─────────────────────────────────────────────

interface FinanceEmptyOnboardingProps {
  readonly onConnect: () => void;
}

interface PlaceholderCardSpec {
  readonly title: string;
  readonly subtitle: string;
}

/**
 * Info-rich empty state for users without any finance connector wired.
 * Replaces the previous bare ``SurfaceEmptyState`` with a strip + three
 * placeholder cards that show WHAT data will appear once the operator
 * collegates a connector. No fake numbers — the cards only describe the
 * source needed for each KPI.
 */
const FinanceEmptyOnboarding: React.FC<FinanceEmptyOnboardingProps> = ({ onConnect }) => {
  const { i18n } = useLingui();
  const PLACEHOLDER_CARDS: readonly PlaceholderCardSpec[] = [
    { title: i18n._(msg`Crediti aperti`), subtitle: i18n._(msg`collegabile via Fatture in Cloud`) },
    { title: i18n._(msg`Debiti aperti`), subtitle: i18n._(msg`collegabile via Fatture in Cloud`) },
    { title: i18n._(msg`Pipeline`), subtitle: i18n._(msg`collegabile via Salesforce`) },
  ];
  return (
    <div className="space-y-4">
      <HighlightNote tone="neutral">
        <Trans>
          Connetti Fatture in Cloud, la tua banca o Salesforce per vedere il tuo flusso di cassa in
          tempo reale.
        </Trans>
      </HighlightNote>
      <div className="grid grid-cols-3 gap-3">
        {PLACEHOLDER_CARDS.map((c) => (
          <div
            key={c.title}
            className="rounded-2xl border border-hairline border-dashed p-4 text-center"
          >
            <div className="mb-1 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
              {c.title}
            </div>
            <div className="font-semibold text-[18px] text-ink-3 tabular-nums tracking-[-0.02em]">
              —
            </div>
            <div className="mt-1 text-[10px] text-ink-3">{c.subtitle}</div>
          </div>
        ))}
      </div>
      <SurfaceEmptyState
        icon={<Wallet size={18} strokeWidth={1.75} />}
        title={i18n._(msg`Collega un connettore finanziario`)}
        description={i18n._(
          msg`La vista cash flow si abilita appena colleghi Fatture in Cloud (fatture), l'open banking (movimenti bancari) o Salesforce (pipeline).`,
        )}
        action={
          <Button variant="primary" size="md" onClick={onConnect}>
            <LinkIcon aria-hidden />
            <Trans>Collega un connettore</Trans>
          </Button>
        }
      />
    </div>
  );
};
FinanceEmptyOnboarding.displayName = "FinanceEmptyOnboarding";

// ---------------------------------------------------------------------------
// Picker-filtered empty state — the user HAS finance connections but has
// untoggled every account in the picker. We keep the header + picker in
// place (no layout collapse) and offer a one-click "select all" affordance
// so the operator can recover without re-opening the popover.
// ---------------------------------------------------------------------------

interface FinanceNoActiveSelectionProps {
  readonly onSelectAll: () => void;
}

const FinanceNoActiveSelection: React.FC<FinanceNoActiveSelectionProps> = ({ onSelectAll }) => {
  const { i18n } = useLingui();
  return (
    <div className="mt-2">
      <SurfaceEmptyState
        icon={<Wallet size={18} strokeWidth={1.75} />}
        title={i18n._(msg`Nessun collegamento attivo`)}
        description={i18n._(
          msg`Hai disattivato tutte le fonti dati. Riattivane almeno una per vedere i pannelli Banking, SDI / Fatture e CRM.`,
        )}
        action={
          <Button variant="primary" size="md" onClick={onSelectAll}>
            <Trans>Attiva tutto</Trans>
          </Button>
        }
      />
    </div>
  );
};
FinanceNoActiveSelection.displayName = "FinanceNoActiveSelection";

// ---------------------------------------------------------------------------
// Patrimonio netto CTA — inline form that collects the equity snapshot the
// Codice della Crisi "Adeguati Assetti" indicator requires.
//
// The flow is intentionally lightweight: an "+ Aggiungi snapshot patrimonio
// netto" affordance expands into two inputs (EUR + date) and a primary
// action. On submit we POST to `/finance/balance-snapshot`; the hook then
// invalidates the cached overview so the new indicator paints automatically.
// ---------------------------------------------------------------------------

const BalanceSnapshotInlineForm: React.FC = () => {
  const { i18n } = useLingui();
  const [expanded, setExpanded] = useState(false);
  const [equityEur, setEquityEur] = useState<string>("");
  const [referenceDate, setReferenceDate] = useState<string>(() =>
    new Date().toISOString().slice(0, 10),
  );
  const mutation = useCreateBalanceSnapshot();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const parsedEur = Number.parseFloat(equityEur.replace(",", "."));
    if (!Number.isFinite(parsedEur) || !referenceDate) {
      return;
    }
    mutation.mutate({
      reference_date: referenceDate,
      equity_eur_cents: Math.round(parsedEur * 100),
    });
  };

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-[8px] border border-ink-3/40 border-dashed bg-transparent px-2.5 py-1.5 font-medium text-[12px] text-ink-2 transition-colors hover:bg-ink-3/5"
      >
        <Trans>+ Aggiungi snapshot patrimonio netto</Trans>
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-2 rounded-[10px] border border-ink-3/20 bg-paper/40 p-3"
    >
      <label className="flex flex-col gap-1 text-[11px] text-ink-2">
        <Trans>Patrimonio netto (€)</Trans>
        <input
          type="number"
          step="0.01"
          value={equityEur}
          onChange={(e) => setEquityEur(e.target.value)}
          required
          className="rounded-[6px] border border-ink-3/30 bg-paper px-2 py-1 text-[13px] text-ink-1 outline-none focus:border-ink-2"
        />
      </label>
      <div className="flex flex-col gap-1 text-[11px] text-ink-2">
        <span>
          <Trans>Data riferimento</Trans>
        </span>
        <DateTimePicker
          value={ymdToDate(referenceDate)}
          onChange={(d) => setReferenceDate(d ? dateToYmd(d) : "")}
          ariaLabel={i18n._(msg`Data di riferimento dello snapshot`)}
        />
      </div>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="rounded-[6px] px-2 py-1 text-[12px] text-ink-3 hover:text-ink-2"
        >
          <Trans>Annulla</Trans>
        </button>
        <button
          type="submit"
          disabled={mutation.isPending}
          className="rounded-[6px] bg-ink-2 px-3 py-1 text-[12px] text-white disabled:opacity-50"
        >
          {mutation.isPending ? i18n._(msg`Salvataggio…`) : i18n._(msg`Salva`)}
        </button>
      </div>
      {mutation.isError ? (
        <p className="text-[11px] text-red-700">
          <Trans>Impossibile salvare lo snapshot. Riprova fra qualche istante.</Trans>
        </p>
      ) : null}
    </form>
  );
};
BalanceSnapshotInlineForm.displayName = "BalanceSnapshotInlineForm";

// ---------------------------------------------------------------------------
// FinanceConsentBanners — surfaces a ``<ConsentExpiryBanner>`` for each
// finance connection whose consent expires within 14 days. Capability-only
// labelling (no vendor names) keeps the alerts white-label safe. Hidden
// silently when nothing is expiring (no Cry Wolf).
// ---------------------------------------------------------------------------

// SMB-italian lessico (UI/UX audit 2026-05-30): "Contabilità" is
// commercialista-speak the imprenditore PMI rarely uses. "Vendite" /
// "Banca" / "Fisco" mirror the new sub-tab chip labels so the consent
// expiry banner reads the same vocabulary across the hub.
const FINANCE_CAPABILITY_LABEL_MSG: Readonly<
  Record<FinanceConnection["capability"], ReturnType<typeof msg>>
> = {
  open_banking: msg`Banca`,
  accounting: msg`Fatture`,
  crm: msg`CRM`,
  sdi_invoicing: msg`Fatturazione elettronica`,
  cassetto_fiscale: msg`Cassetto Fiscale`,
};

interface FinanceConsentBannersProps {
  readonly connections: readonly FinanceConnection[];
  readonly onRenew: () => void;
}

const FinanceConsentBanners: React.FC<FinanceConsentBannersProps> = ({ connections, onRenew }) => {
  const { i18n } = useLingui();
  // Filter inline — the connections array is bounded by user license tier
  // (a handful of rows) so memoisation is overkill. Avoiding ``useMemo``
  // also keeps the dep array free of a fresh-every-render ``Date``.
  //
  // June 4 2026 — reads the REAL consent expiry, not the OAuth access
  // token. PSD2 banking + AdE Cassetto Fiscale populate this; FiC
  // accounting / Salesforce CRM / SDI invoicing leave it null and the
  // banner stays correctly silent for them.
  const expiring = connections.filter((c) => consentExpiryUrgency(c.consent_expires_at) !== null);
  if (expiring.length === 0) return null;
  return (
    <div className="mb-4 flex flex-col gap-2">
      {expiring.map((c) => (
        <ConsentExpiryBanner
          key={c.id}
          expiresAt={c.consent_expires_at}
          capabilityLabel={`${i18n._(FINANCE_CAPABILITY_LABEL_MSG[c.capability])} — ${c.account_label}`}
          onRenew={onRenew}
        />
      ))}
    </div>
  );
};
FinanceConsentBanners.displayName = "FinanceConsentBanners";

// ---------------------------------------------------------------------------
// CassettoAskAndoBlock — small inline panel surfaced inside the Finance hub
// when the Cassetto Fiscale connector is wired. We don't ship a dedicated
// drill-down route (no BE list-endpoint, no v1 precedent for fiscal browser
// surfaces — see UI/UX expert audit 2026-05-30); instead we hand the user
// off to the chat with a pre-typed prompt so the new
// ``CassettoFiscaleSpecialist`` answers natural-language queries against
// the AdE archive. Deep-link contract: ``/workspace/conversations?prompt=…``
// (validator clamps at 2000 chars + clears the param on mount).
// ---------------------------------------------------------------------------

interface CassettoAskAndoBlockProps {
  readonly navigate: ReturnType<typeof useNavigate>;
  readonly primary: string;
  readonly prompt: string;
  readonly cta: string;
}

const CassettoAskAndoBlock: React.FC<CassettoAskAndoBlockProps> = ({
  navigate,
  primary,
  prompt,
  cta,
}) => {
  return (
    <div className="flex flex-col gap-2.5">
      <p className="text-ink-2 text-sm">{primary}</p>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() =>
          void navigate({
            to: "/workspace/conversations",
            search: (prev) => ({ ...prev, prompt }),
          })
        }
        className="w-fit gap-1.5 text-accent-11 hover:bg-accent-2/40"
      >
        <Sparkles className="h-3.5 w-3.5" aria-hidden />
        {cta}
      </Button>
    </div>
  );
};
CassettoAskAndoBlock.displayName = "CassettoAskAndoBlock";

// ---------------------------------------------------------------------------
// Tab bodies — June 2026 density pass. Each tab now ships:
//   1. ``ConnectionsPanel``  (per-tab capability-filtered list)
//   2. tab-specific KPI strip / breakdown table
//   3. ``AskAndoCardGrid``   (Italian quick-prompts that deep-link chat)
//
// The BE doesn't expose per-account REST endpoints for transactions /
// invoices / cassetto rows yet — the AI specialists answer those via
// chat, so the Ask-Ando cards are the bridge.
// ---------------------------------------------------------------------------

interface BankingTabBodyProps {
  readonly connections: readonly FinanceConnection[];
  readonly cashFlow: readonly CashFlowBucket[];
  readonly burnRate: BurnRateMetric | null;
  readonly currency: string;
  readonly onConnect: () => void;
}

const BankingTabBody: React.FC<BankingTabBodyProps> = ({
  connections,
  cashFlow,
  burnRate,
  currency,
  onConnect,
}) => {
  const { i18n } = useLingui();

  // Real-data hooks — gated on the user having at least one
  // ``open_banking`` connection so we don't fire requests against
  // endpoints that will 200-with-empty for unconnected tenants.
  const hasBank = connections.length > 0;
  const bankingSummary = useBankingSummary(currency, hasBank);
  const bankAccounts = useBankAccounts({ enabled: hasBank });
  const monthlyAgg = useMonthlyAggregates({
    currency,
    months: 1,
    direction: "out",
    enabled: hasBank,
  });
  const recentTx = useRecentTransactions({ limit: 6, enabled: hasBank });
  const triggerSync = useTriggerBankSync();
  const triggerAllSync = useTriggerAllBankSync();

  const totalBalanceCents = bankingSummary.data?.total_balance_cents ?? 0;
  const inflowMonth = bankingSummary.data?.current_month_in_cents ?? 0;
  const outflowMonth = Math.abs(bankingSummary.data?.current_month_out_cents ?? 0);
  const accountsCount = bankingSummary.data?.accounts_count ?? 0;

  // Sparse-data signal: connected but the BE returned zero balance AND
  // zero monthly inflow/outflow. Typical for sandbox A-Cube tenants
  // before the first real movement lands.
  const sparseSandbox =
    hasBank &&
    !bankingSummary.isLoading &&
    totalBalanceCents === 0 &&
    inflowMonth === 0 &&
    outflowMonth === 0 &&
    accountsCount > 0;

  return (
    <>
      <ConnectionsPanel
        title={i18n._(msg`Conti bancari collegati`)}
        connections={connections}
        emptyTitle={i18n._(msg`Nessun conto bancario collegato`)}
        emptyDescription={i18n._(
          msg`Collega il tuo open banking (PSD2) per vedere saldi, movimenti e categorie aggiornati ogni poche ore.`,
        )}
        onConnect={onConnect}
      />

      {/* ── Diagnostic sync banner — answers "perché non vedo movimenti?"
            with the real per-account last_synced state + a one-click
            "Sync ora" that fans out to every account in parallel and
            refreshes the dependent queries on completion. */}
      {hasBank ? (
        <SyncStatusPanel
          accounts={bankAccounts.data ?? []}
          transactionsCount={recentTx.transactions.length}
          isSyncInFlight={triggerAllSync.isPending || triggerSync.isPending}
          onSyncAll={() => {
            const ids = (bankAccounts.data ?? []).map((a) => a.id);
            if (ids.length > 0) triggerAllSync.mutate(ids);
          }}
        />
      ) : null}

      {/* ── Saldo + KPI mensili (dati REALI da /banking/summary) ── */}
      {hasBank ? (
        <Panel>
          <PanelHead
            left={
              <>
                <div className="mb-2 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
                  <Trans>Saldo totale</Trans>
                </div>
                <div className="font-semibold text-[20px] text-ink-1 tabular-nums tracking-[-0.02em]">
                  {bankingSummary.isLoading
                    ? "…"
                    : formatCurrencyFromCents(totalBalanceCents, currency)}
                </div>
                <div className="mt-0.5 text-[11px] text-ink-3 tabular-nums">
                  {accountsCount === 0
                    ? i18n._(msg`Nessun conto attivo`)
                    : accountsCount === 1
                      ? i18n._(msg`1 conto attivo`)
                      : i18n._(msg`${accountsCount} conti attivi`)}
                </div>
              </>
            }
          />
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <MiniMetric
              label={i18n._(msg`Entrate mese`)}
              value={formatCurrencyFromCents(inflowMonth, currency)}
              tone="success"
            />
            <MiniMetric
              label={i18n._(msg`Uscite mese`)}
              value={formatCurrencyFromCents(outflowMonth, currency)}
              tone="danger"
            />
            <MiniMetric
              label={i18n._(msg`Burn mensile`)}
              value={formatCurrency(burnRate?.monthly_eur, currency)}
              sub={
                nullableNum(burnRate?.trend_delta_30d) !== null
                  ? i18n._(msg`Δ30g ${formatCurrencyDelta(burnRate?.trend_delta_30d, currency)}`)
                  : undefined
              }
              tone="neutral"
            />
            {(() => {
              const runway = nullableNum(burnRate?.runway_months);
              const tone: "neutral" | "success" | "warn" | "danger" =
                runway === null
                  ? "neutral"
                  : runway >= 12
                    ? "success"
                    : runway >= 6
                      ? "warn"
                      : "danger";
              return (
                <MiniMetric
                  label={i18n._(msg`Runway`)}
                  value={runway !== null ? i18n._(msg`${formatNumber(runway, 1)} mesi`) : "—"}
                  sub={
                    runway === null
                      ? undefined
                      : runway >= 12
                        ? i18n._(msg`oltre l'anno`)
                        : i18n._(msg`sotto l'anno`)
                  }
                  tone={tone}
                />
              );
            })()}
          </div>
          {sparseSandbox ? (
            <HighlightNote tone="neutral" className="mt-3">
              <Trans>
                Conti collegati ma nessun movimento ancora. Il provider potrebbe essere in sandbox o
                la prima sync deve ancora completare — forza un aggiornamento sul conto qui sotto.
              </Trans>
            </HighlightNote>
          ) : null}
        </Panel>
      ) : null}

      {/* ── Conti — sempre visibile quando hasBank ─────────────────
            Mostra accounts reali con saldo + last-synced. In loading
            renderizza skeleton; in error mostra retry; quando vuoto
            (provider in sandbox / accounts paused) surface un hint
            esplicito invece di nascondere il pannello. */}
      {hasBank ? (
        <Panel>
          <PanelHead
            left={
              <div className="font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
                <Trans>Conti</Trans>
              </div>
            }
            right={
              bankAccounts.isError ? (
                <button
                  type="button"
                  onClick={() => void bankAccounts.refetch()}
                  className="text-[10.5px] text-accent underline-offset-2 hover:underline"
                >
                  <Trans>Riprova</Trans>
                </button>
              ) : null
            }
          />
          {bankAccounts.isLoading ? (
            <BankingPanelSkeleton rows={2} />
          ) : bankAccounts.isError ? (
            <BankingPanelError
              message={i18n._(msg`Impossibile caricare i conti dal provider banking.`)}
            />
          ) : (bankAccounts.data?.length ?? 0) === 0 ? (
            <BankingPanelEmpty
              message={i18n._(
                msg`Nessun conto ancora attivo. Il provider potrebbe non aver completato la prima sync.`,
              )}
            />
          ) : (
            <BankAccountsList
              accounts={bankAccounts.data ?? []}
              onTriggerSync={(id) => triggerSync.mutate(id)}
              syncingId={triggerSync.isPending ? (triggerSync.variables ?? null) : null}
            />
          )}
        </Panel>
      ) : null}

      {/* ── Movimenti recenti — sempre visibile quando hasBank ──── */}
      {hasBank ? (
        <Panel>
          <PanelHead
            left={
              <div className="font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
                <Trans>Movimenti recenti</Trans>
              </div>
            }
            right={
              recentTx.transactions.length > 0 ? (
                <span className="text-[10.5px] text-ink-3 tabular-nums">
                  <Trans>Ultimi {recentTx.transactions.length}</Trans>
                </span>
              ) : null
            }
          />
          {recentTx.isLoading ? (
            <BankingPanelSkeleton rows={4} />
          ) : recentTx.isError ? (
            <BankingPanelError message={i18n._(msg`Errore nel caricamento dei movimenti.`)} />
          ) : recentTx.transactions.length === 0 ? (
            <BankingPanelEmpty
              message={i18n._(
                msg`Nessun movimento nel periodo. Il provider potrebbe essere in sandbox o la sincronizzazione è in corso.`,
              )}
            />
          ) : (
            <RecentTransactionsList transactions={recentTx.transactions} />
          )}
        </Panel>
      ) : null}

      {/* ── Top categorie di spesa — sempre visibile quando hasBank ── */}
      {hasBank ? (
        <Panel>
          <PanelHead
            left={
              <div className="font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
                <Trans>Dove hai speso (mese corrente)</Trans>
              </div>
            }
          />
          {monthlyAgg.isLoading ? (
            <BankingPanelSkeleton rows={3} />
          ) : monthlyAgg.isError ? (
            <BankingPanelError message={i18n._(msg`Errore nel caricamento delle categorie.`)} />
          ) : (monthlyAgg.data?.length ?? 0) === 0 ? (
            <BankingPanelEmpty
              message={i18n._(
                msg`Nessuna spesa nel mese. Le categorie compaiono quando arrivano i primi movimenti di uscita.`,
              )}
            />
          ) : (
            <CategorySpendBreakdown rows={monthlyAgg.data ?? []} currency={currency} />
          )}
        </Panel>
      ) : null}

      {/* ── Cash flow settimanale (dal /finance/overview) ─────────── */}
      {cashFlow.length > 0 ? (
        <Panel>
          <div className="mb-3 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
            <Trans>Cash flow settimanale</Trans>
          </div>
          <CashFlowWeekTable buckets={cashFlow} currency={currency} />
        </Panel>
      ) : null}

      <Panel>
        <AskAndoSectionLabel>
          <Trans>Chiedi ad Ando · Banca</Trans>
        </AskAndoSectionLabel>
        <AskAndoCardGrid
          cards={[
            {
              title: i18n._(msg`Pagamenti ricorrenti degli ultimi 3 mesi`),
              prompt: i18n._(
                msg`Mostrami i pagamenti ricorrenti degli ultimi 3 mesi raggruppati per fornitore.`,
              ),
            },
            {
              title: i18n._(msg`Top 10 spese del mese con causale`),
              prompt: i18n._(
                msg`Quali sono le 10 spese più alte di questo mese? Mostrami importo, data e causale.`,
              ),
            },
            {
              title: i18n._(msg`Entrate più grandi delle ultime 4 settimane`),
              prompt: i18n._(
                msg`Mostrami le entrate più grandi delle ultime 4 settimane raggruppate per cliente.`,
              ),
            },
          ]}
        />
      </Panel>
    </>
  );
};
BankingTabBody.displayName = "BankingTabBody";

interface SalesTabBodyProps {
  readonly connections: readonly FinanceConnection[];
  readonly receivables: OpenReceivablesSummary | null;
  readonly payables: OpenPayablesSummary | null;
  readonly pipeline: SalesforcePipelineSummary | null;
  readonly currency: string;
  readonly onConnect: () => void;
}

const SalesTabBody: React.FC<SalesTabBodyProps> = ({
  connections,
  receivables,
  payables,
  pipeline,
  currency,
  onConnect,
}) => {
  const { i18n } = useLingui();
  return (
    <>
      <ConnectionsPanel
        title={i18n._(msg`Fatturazione e CRM collegati`)}
        connections={connections}
        emptyTitle={i18n._(msg`Nessun gestionale o CRM collegato`)}
        emptyDescription={i18n._(
          msg`Collega Fatture in Cloud per crediti/debiti, SDI per le e-fatture e Salesforce per la pipeline commerciale.`,
        )}
        onConnect={onConnect}
      />

      {receivables || payables || pipeline ? (
        <Panel>
          <div className="mb-3 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
            <Trans>Indicatori vendite</Trans>
          </div>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            {receivables ? (
              <>
                <MiniMetric
                  label={i18n._(msg`Crediti aperti`)}
                  value={formatCurrencyFromCents(receivables.total_eur_cents, currency)}
                  sub={i18n._(msg`${receivables.invoice_count} fatture`)}
                  tone="neutral"
                />
                <MiniMetric
                  label={i18n._(msg`In scadenza 30g`)}
                  value={String(receivables.expiring_soon_count)}
                  sub={
                    receivables.severely_overdue_count > 0
                      ? i18n._(msg`${receivables.severely_overdue_count} > 60g`)
                      : undefined
                  }
                  tone={
                    receivables.severely_overdue_count > 0
                      ? "danger"
                      : receivables.expiring_soon_count > 0
                        ? "warn"
                        : "success"
                  }
                />
              </>
            ) : null}
            {payables ? (
              <MiniMetric
                label={i18n._(msg`Debiti aperti`)}
                value={formatCurrencyFromCents(payables.total_eur_cents, currency)}
                sub={i18n._(msg`${payables.invoice_count} fatture passive`)}
                tone="neutral"
              />
            ) : null}
            {pipeline ? (
              <MiniMetric
                label={i18n._(msg`Pipeline aperta`)}
                value={formatCurrencyFromCents(pipeline.total_eur_cents, currency)}
                sub={i18n._(
                  msg`${pipeline.deal_count} deal · ${pipeline.closing_soon_count} in chiusura`,
                )}
                tone="accent"
              />
            ) : null}
          </div>
        </Panel>
      ) : null}

      <Panel>
        <AskAndoSectionLabel>
          <Trans>Chiedi ad Ando · Fatture</Trans>
        </AskAndoSectionLabel>
        <AskAndoCardGrid
          cards={[
            {
              title: i18n._(msg`Clienti con fatture scadute da oltre 30 giorni`),
              prompt: i18n._(
                msg`Quali clienti hanno fatture scadute da oltre 30 giorni? Mostrami nome, totale e ultima scadenza.`,
              ),
            },
            {
              title: i18n._(msg`Fatture attive in scadenza nei prossimi 7 giorni`),
              prompt: i18n._(
                msg`Mostrami le fatture attive in scadenza nei prossimi 7 giorni con importo e cliente.`,
              ),
            },
            {
              title: i18n._(msg`Deal Salesforce in chiusura nei prossimi 30 giorni`),
              prompt: i18n._(
                msg`Mostrami le opportunità Salesforce in chiusura nei prossimi 30 giorni con importo, stage e probabilità.`,
              ),
            },
          ]}
        />
      </Panel>
    </>
  );
};
SalesTabBody.displayName = "SalesTabBody";

interface FiscalTabBodyProps {
  readonly connections: readonly FinanceConnection[];
  readonly deadlines: readonly FinanceDeadline[];
  readonly cassettoConnected: boolean;
  readonly onConnect: () => void;
}

const FiscalTabBody: React.FC<FiscalTabBodyProps> = ({
  connections,
  deadlines,
  cassettoConnected,
  onConnect,
}) => {
  const { i18n } = useLingui();
  return (
    <>
      <ConnectionsPanel
        title={i18n._(msg`Cassetto fiscale e SDI`)}
        connections={connections}
        emptyTitle={i18n._(msg`Nessun connettore fiscale collegato`)}
        emptyDescription={i18n._(
          msg`Collega il Cassetto Fiscale (delega AdE) per interrogare F24, IVA e fatture elettroniche via chat.`,
        )}
        onConnect={onConnect}
      />

      <Panel>
        <div className="mb-3 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.1em]">
          <Trans>Prossime scadenze fiscali</Trans>
        </div>
        {deadlines.length > 0 ? (
          <div className="flex flex-col">
            {deadlines.map((d, idx) => (
              <DeadlineRow key={`${d.kind}-${d.due_date}-${idx}`} item={d} />
            ))}
          </div>
        ) : cassettoConnected ? (
          <p className="text-ink-3 text-sm">
            <Trans>
              Nessuna scadenza nei prossimi 30 giorni. Interroga il Cassetto via chat per uno
              storico completo.
            </Trans>
          </p>
        ) : (
          <p className="text-ink-3 text-sm">
            <Trans>
              Collega il Cassetto Fiscale per visualizzare F24, LIPE e conservazione in arrivo.
            </Trans>
          </p>
        )}
      </Panel>

      <Panel>
        <AskAndoSectionLabel>
          <Trans>Chiedi ad Ando · Fisco</Trans>
        </AskAndoSectionLabel>
        <AskAndoCardGrid
          cards={[
            {
              title: i18n._(msg`F24 in scadenza nei prossimi 30 giorni`),
              prompt: i18n._(
                msg`Mostrami gli F24 in scadenza nei prossimi 30 giorni dal Cassetto Fiscale con tributo, importo e data.`,
              ),
            },
            {
              title: i18n._(msg`Liquidazione IVA del trimestre`),
              prompt: i18n._(
                msg`Qual è la liquidazione IVA del trimestre corrente? Mostrami debito/credito e scadenze versamento.`,
              ),
            },
            {
              title: i18n._(msg`Conservazione e LIPE nei prossimi 60 giorni`),
              prompt: i18n._(
                msg`Quali adempimenti di conservazione e quali LIPE devo presentare nei prossimi 60 giorni?`,
              ),
            },
          ]}
        />
      </Panel>
    </>
  );
};
FiscalTabBody.displayName = "FiscalTabBody";

// ---------------------------------------------------------------------------
// Banking panel state primitives — shared loading/empty/error skin used by
// every panel inside the Banca tab so the user always knows WHY a panel is
// silent (loading vs sandbox vs error) instead of seeing nothing.
// ---------------------------------------------------------------------------

interface BankingPanelSkeletonProps {
  readonly rows?: number;
}

const BankingPanelSkeleton: React.FC<BankingPanelSkeletonProps> = ({ rows = 3 }) => (
  <div className="flex flex-col gap-2" aria-busy="true">
    {Array.from({ length: rows }).map((_, idx) => (
      <div
        key={`bsk-${idx}`}
        className="flex items-center gap-3 rounded-md bg-black/[0.03] px-3 py-2.5"
      >
        <span className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-black/[0.06] motion-reduce:animate-none" />
        <span className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="h-2 w-1/3 animate-pulse rounded bg-black/[0.06] motion-reduce:animate-none" />
          <span className="h-2.5 w-2/3 animate-pulse rounded bg-black/[0.06] motion-reduce:animate-none" />
        </span>
      </div>
    ))}
  </div>
);
BankingPanelSkeleton.displayName = "BankingPanelSkeleton";

interface BankingPanelEmptyProps {
  readonly message: string;
}

const BankingPanelEmpty: React.FC<BankingPanelEmptyProps> = ({ message }) => (
  <p className="text-[11.5px] text-ink-3 leading-snug">{message}</p>
);
BankingPanelEmpty.displayName = "BankingPanelEmpty";

interface BankingPanelErrorProps {
  readonly message: string;
}

const BankingPanelError: React.FC<BankingPanelErrorProps> = ({ message }) => (
  <p className="text-[11.5px] text-rose-700">{message}</p>
);
BankingPanelError.displayName = "BankingPanelError";
